

import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createPermission } from "../redux/actions/roleActions";
import { 
  FiPlus, 
  FiCheck, 
  FiArrowLeft, 
  FiKey, 
  FiFileText, 
  FiAlertCircle,
  FiShield,
  FiLock
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";

const CreatePermission = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error, success } = useSelector((state) => state.role);

  const [formData, setFormData] = useState({
    permission_name: "",
    description: ""
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.permission_name.trim()) return;

    const payload = {
      permission_name: formData.permission_name.trim(),
      description: formData.description.trim()
    };

    dispatch(createPermission(payload));
  };

  // Reset form when creation is successful
  React.useEffect(() => {
    if (success && !loading) {
      setFormData({
        permission_name: "",
        description: ""
      });
    }
  }, [success, loading]);

  const isFormValid = formData.permission_name.trim() !== "";

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400 text-lg">Creating Permission...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        {/* <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate("/dashboard/permissions")}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <FiArrowLeft className="text-lg" />
            Back to Permissions
          </button>
        </div> */}

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Form Header */}
          <div className="bg-gradient-to-r from-purple-500 to-pink-500 px-6 py-8">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <FiKey className="text-2xl text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white mb-2">
                  Create Permission
                </h1>
                <p className="text-purple-100">
                  Add a new permission to control access and functionality
                </p>
              </div>
            </div>
          </div>

          {/* Form Content */}
          <div className="p-6">
            {/* Success Message */}
            {success && (
              <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                    <FiCheck className="text-white text-sm" />
                  </div>
                  <div>
                    <p className="text-green-800 dark:text-green-300 font-medium">
                      Permission created successfully!
                    </p>
                    <p className="text-green-600 dark:text-green-400 text-sm">
                      The new permission has been added to the system.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                    <FiAlertCircle className="text-white text-sm" />
                  </div>
                  <div>
                    <p className="text-red-800 dark:text-red-300 font-medium">
                      {typeof error === "string" ? error : error?.message || "Something went wrong"}
                    </p>
                    <p className="text-red-600 dark:text-red-400 text-sm mt-1">
                      Please check your input and try again.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 gap-6">
                {/* Permission Name */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <div className="flex items-center gap-2">
                      <FiLock className="text-sm" />
                      Permission Name <span className="text-red-500">*</span>
                    </div>
                  </label>
                  <input
                    type="text"
                    value={formData.permission_name}
                    onChange={(e) => handleChange("permission_name", e.target.value)}
                    placeholder="Enter permission name (e.g., user.create, task.delete, report.view, etc.)"
                    className="w-full bg-white dark:bg-gray-700 border-0 rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 transition placeholder-gray-500 dark:placeholder-gray-400"
                    required
                    disabled={loading}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Use dot notation for better organization (e.g., module.action)
                  </p>
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <div className="flex items-center gap-2">
                      <FiFileText className="text-sm" />
                      Description
                    </div>
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleChange("description", e.target.value)}
                    placeholder="Enter permission description (optional) - explain what this permission allows users to do..."
                    rows={4}
                    className="w-full bg-white dark:bg-gray-700 border-0 rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 transition placeholder-gray-500 dark:placeholder-gray-400 resize-none"
                    disabled={loading}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Optional: Provide details about what this permission enables
                  </p>
                </div>
              </div>

              {/* Common Permissions Preview */}
              <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl p-4">
                <h4 className="text-sm font-medium text-purple-900 dark:text-purple-100 mb-3 flex items-center gap-2">
                  <FiShield className="text-sm" />
                  Common Permission Patterns
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {[
                    'user.create', 'user.read', 'user.update', 'user.delete',
                    'task.create', 'task.read', 'task.update', 'task.delete',
                    'report.view', 'report.export', 'settings.manage', 'admin.access'
                  ].map((permission) => (
                    <button
                      key={permission}
                      type="button"
                      onClick={() => handleChange("permission_name", permission)}
                      className="text-xs bg-white dark:bg-gray-700 border border-purple-200 dark:border-purple-800 text-purple-700 dark:text-purple-300 py-2 px-3 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/30 transition-colors text-center truncate"
                      title={permission}
                    >
                      {permission}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-purple-600 dark:text-purple-400 mt-2">
                  Click on any permission above to quickly fill the permission name field
                </p>
              </div>

              {/* Permission Categories */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2 flex items-center gap-2">
                  <FiKey className="text-sm" />
                  Permission Categories
                </h4>
                <div className="space-y-2">
                  {[
                    { category: 'User Management', examples: 'user.create, user.read, user.update, user.delete' },
                    { category: 'Task Management', examples: 'task.create, task.read, task.update, task.delete' },
                    { category: 'Reporting', examples: 'report.view, report.export, report.manage' },
                    { category: 'System Administration', examples: 'settings.manage, admin.access, system.config' }
                  ].map((item, index) => (
                    <div key={index} className="text-sm">
                      <span className="font-medium text-blue-800 dark:text-blue-200">{item.category}:</span>
                      <span className="text-blue-600 dark:text-blue-400 ml-2">{item.examples}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    setFormData({
                      permission_name: "",
                      description: ""
                    });
                  }}
                  disabled={loading}
                  className="flex items-center justify-center gap-2 bg-gray-500 hover:bg-gray-600 text-white py-3 px-6 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Clear Form
                </button>
                
                <button
                  type="submit"
                  disabled={loading || !isFormValid}
                  className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white py-3 px-6 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Creating Permission...
                    </>
                  ) : (
                    <>
                      <FiCheck className="text-lg" />
                      Create Permission
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Form Tips */}
        <div className="mt-6 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-100 mb-3">
            💡 Permission Creation Guidelines
          </h3>
          <ul className="text-purple-800 dark:text-purple-200 text-sm space-y-2">
            <li>• Use consistent naming conventions with dot notation (module.action)</li>
            <li>• Common patterns: create, read, update, delete, view, export, manage</li>
            <li>• Keep permission names descriptive and self-explanatory</li>
            <li>• Group related permissions by module (user., task., report., etc.)</li>
            <li>• Consider your application's functionality when creating permissions</li>
            <li>• Permissions can be assigned to roles after creation</li>
            <li>• Use descriptions to clarify the scope of each permission</li>
            <li>• Fields marked with * are required</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CreatePermission;